// Third-party
import { get, isEqual } from 'lodash';

// Types
import ComparisonItem from '@/types/comparison-item';
import CustomDisclaimerHolding from '@/types/custom-disclaimer-holding';
import {
  DAOType,
  DataView,
  LayoutType,
  ProposalSectionType,
  ProposalSidebarControl,
  ProposalTemplateElementType,
  ProposalTemplateSectionElement,
} from '@/modules/proposal/Proposal';
import Locale from '@/types/locale';
import ProposalOrientation from '@/types/proposal-orientation';

// Utils
import { DISCLAIMER_TAG } from '@/utils/disclaimers/constants';
import { formatLocalizedString, formatMessageWithLocale, LOCALE } from '@/utils/i18n';
import { PROPOSAL_DISCLAIMERS, PROPOSAL_ORIENTATION } from '@/modules/proposal/utils/constants';

type DisclaimerHolder = {
  showFooter?: boolean;
  showHeader?: boolean;
  text: string;
  title: string;
};

class ProposalSectionBuilder {
  locale: Locale;

  newSections: ProposalTemplateSectionElement[] = [];

  orientation: ProposalOrientation;

  proposalItems: ComparisonItem[];

  templateKey?: string;

  constructor(proposalItems: ComparisonItem[], orientation: ProposalOrientation, locale: Locale, templateKey?: string) {
    this.locale = locale;
    this.orientation = orientation;
    this.proposalItems = proposalItems;
    this.templateKey = templateKey;
  }

  splitIntoPages = (): DisclaimerHolder[][] => {
    const CHARS_PER_LINE = this.orientation === PROPOSAL_ORIENTATION.PORTRAIT ? 140 : 225;
    const PIXELS_PER_PAGE = this.orientation === PROPOSAL_ORIENTATION.PORTRAIT ? 1100 : 800; // to adjust
    const PIXELS_PER_LINE = 20;
    const PIXELS_PER_HEADER = 22;

    // Keep track of already added holdings
    const addedHoldings: CustomDisclaimerHolding[] = [];
    // Keep track of current page index
    let currentPageIndex: number = 0;
    // Initialize pages list with an empty page
    const pages: DisclaimerHolder[][] = [[]];
    // Keep track of the % of remaining space on the current page
    let remainingAllocation: number = 100;

    for (const item of this.proposalItems) {
      if (item.disclaimers != null) {
        const customDisclaimer = item.disclaimers[PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER];
        const hasCustomDisclaimers = get(customDisclaimer, ['data', 'holdings'], []).length > 0;

        if (hasCustomDisclaimers) {
          const customDisclaimerHoldings = customDisclaimer?.data?.holdings ?? [];
          const proposalDisclaimerHoldings = customDisclaimerHoldings?.filter((h: CustomDisclaimerHolding) => h.tags?.includes(DISCLAIMER_TAG.PROPOSAL)) ?? [];

          for (const holding of proposalDisclaimerHoldings) {
            if (!addedHoldings.some(aH => isEqual(aH, holding))) {
              addedHoldings.push(holding);

              // Fits about 140 chars on a line, one line takes about 25px. Calculate space taken from those values.
              // Yes, this seems extremely arbitrary, and that's because it is - but this is probably the best we can do
              // given the resources available.
              const title = formatMessageWithLocale('Disclaimers.CustomDisclaimer.Title', this.locale, {
                items: holding.symbol || holding.name,
              });
              const text = formatLocalizedString(holding.content, this.locale);

              // Checking to see if the instrument has a 'configs.proposals' array, where we would list the specific proposal to display custom disclaimers in
              const showCustomFundDisclaimer: boolean =
                get(holding, ['configs', 'proposals'], []).includes(this.templateKey) || get(holding, ['configs', 'proposals'], []).length === 0;

              if (showCustomFundDisclaimer && !!get(holding, ['configs', 'staticPageDisclaimer'], undefined)) {
                remainingAllocation !== 100 && currentPageIndex++;
                pages[currentPageIndex] = [];
                pages[currentPageIndex].push({ title: '', text, showHeader: false, showFooter: false });
                remainingAllocation = 0;
              } else {
                const paragraphsCount = text.match(/<p>/g)?.length || 1;
                const textSpace = (text.length / CHARS_PER_LINE) * PIXELS_PER_LINE;
                const pageSpace = ((textSpace + paragraphsCount * PIXELS_PER_LINE + PIXELS_PER_HEADER) / PIXELS_PER_PAGE) * 100;

                const currentAllocation = Math.min(pageSpace, 100);

                if (remainingAllocation < currentAllocation) {
                  currentPageIndex++;
                  remainingAllocation = 100;
                  pages[currentPageIndex] = [];
                }

                pages[currentPageIndex].push({ title, text });
                remainingAllocation -= currentAllocation;
              }
            }
          }
        }
      }
    }

    if (pages[0].length === 0) {
      // we do not have a single disclaimer, do not build anything
      return [];
    }

    return pages;
  };

  build = () => {
    const pages = this.splitIntoPages();

    if (pages.length === 0) {
      return [];
    }

    this.newSections = pages.map((page, pageIndex) => {
      const section = {
        type: ProposalTemplateElementType.Section,
        sectionType: ProposalSectionType.InstrumentCustomDisclaimers,
        key: `${ProposalSectionType.InstrumentCustomDisclaimers}_${pageIndex}`,
        forced: true,
        buildRules: [],
        pageFraction: {
          Portrait: 1,
          Landscape: 1,
        },
        label: {
          [LOCALE.EN_CA]: 'Disclaimers',
          [LOCALE.FR_CA]: 'Avis de non-responsabilité',
        },
        sidebarControl: ProposalSidebarControl.Hide,
        showTitle: false,
        showItemNamesInDisclaimers: true,
        layout: {
          type: ProposalTemplateElementType.Layout,
          layout: {
            type: LayoutType.Grid,
            columns: 1,
          },
          elements: [
            {
              type: ProposalTemplateElementType.Data,
              dataView: {
                type: DataView.Html,
              },
              DAO: {
                type: DAOType.HTMLContent,
                htmlContentDAOOptions: {
                  html: page.map(content => {
                    const formatedContent = `
                      <div class="disclaimer-title">
                        <h2>${content.title}</h2>
                      </div>
                      <div class="disclaimer-content" id="static-custom-disclaimer">
                        <p>${content.text}</p>
                      </div>
                    `;

                    return {
                      content: {
                        // content is generated in the right local, we simply pass in the same value.
                        [LOCALE.EN_CA]: formatedContent,
                        [LOCALE.FR_CA]: formatedContent,
                      },
                    };
                  }),
                },
              },
            },
          ],
        },
      } as ProposalTemplateSectionElement;

      if (page[0].hasOwnProperty('showHeader')) {
        section.showHeader = page[0].showHeader;
      }

      if (page[0].hasOwnProperty('showFooter')) {
        section.showFooter = page[0].showFooter;
      }

      return section;
    });
  };
}

export const buildInstrumentCustomDisclaimers = (
  proposalItems: ComparisonItem[],
  orientation: ProposalOrientation,
  locale: Locale,
  templateKey: string,
): ProposalTemplateSectionElement[] => {
  const builder = new ProposalSectionBuilder(proposalItems, orientation, locale, templateKey);
  builder.build();

  return builder.newSections;
};
